import { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import WarningIcon from '@material-ui/icons/Warning';
import DocumentSigner from 'packages/document-signer-react/DocumentSigner';
import Modal from 'components/CncUI/Modal';
import Button from 'components/CncUI/Button';
import Loader from 'components/Loader';

import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';
import useAuthentication from 'hooks/useAuthentication';

import Logger from 'utilities/logger';
import parseDocument from 'pages/Loanout/UploadDocument/parseDocument';
import Typography from 'components/CncUI/Typography';

import 'react-datepicker/dist/react-datepicker.css';

const getActor = (state, user) => {
  const form = state?.form || {};
  const names = (user
    ? [user.firstName, user.lastName]
    : [form.guardianFirstName, form.guardianLastName]
  ).filter(Boolean);

  return {
    name: names.join(' '),
    email: user ? user.email : state?.email,
    initials: names.map(name => name[0]).join(''),
    lastName: user ? user.firstName : form.guardianLastName,
    firstName: user ? user.lastName : form.guardianFirstName,
    uid: user ? user.uid : 'affidavit',
  };
};

const Affidavit = props => {
  const { state, changeState, setStep } = props;
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [show, showDocument] = useState(false);
  const [openModal, showModal] = useState(false);
  const { user } = useAuthentication();
  const logger = Logger('Minors.Registration.Affidavit');

  const actor = getActor(state, user);
  const params = { type: 'crew' };

  const createDocument = useMutation('createAffidavitDocument', {
    scopes: null,
    request: input => ({ input: JSON.stringify(input) }),
    response: res => res.data.createAffidavitDocument || null,
  });

  const submitDocument = useMutation('submitAffidavitDocument', {
    scopes: null,
    request: input => ({ input: JSON.stringify(input) }),
    response: res => res.data.submitAffidavitDocument || false,
  });

  const getDocumentPages = useQuery('getAffidavitDocumentImages', {
    scopes: null,
    request: input => ({ input: JSON.stringify(input) }),
    response: res =>
      (res.data.getAffidavitDocumentImages || []).map(({ url }) => url),
  });

  useEffect(async () => {
    try {
      let response = await createDocument(actor);
      if (!response.document) {
        response = await createDocument({ id: response.id, ...actor });
      }
      if (!response.document) {
        console.error(response);
        setLoading(false);
        return;
      }
      const parsed = parseDocument(response.document);
      changeState({ affidavitDocumentId: response.document.id });
      setTemplate(parsed);
      showModal(!!parsed);
      setLoading(false);
    } catch (err) {
      logger.error(err);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <Loader fullScreen message="Checking information..." />;
  }

  const submit = data => {
    logger.debug('Document Data', { data });
    const id = data.id;
    const fields = data.fields.map(({ id, value }) => ({ id, value }));
    const input = { id, fields, actorInfo: actor };
    submitDocument(input)
      .then(() => {
        logger.debug('Submitted');
        setStep('SearchAccount');
      })
      .catch(err => {
        logger.error(err);
      });
  };

  const openDocument = () => {
    showModal(false);
    showDocument(true);
  };

  const cancel = () => {
    setStep('Error');
  };

  return (
    <>
      <Modal open={openModal} maxWidth={450}>
        <Modal.Title>
          <WarningIcon style={{ verticalAlign: 'top', marginRight: 4 }} />{' '}
          Guardianship Acknowledgment
        </Modal.Title>
        <Modal.Content>
          <Grid container spacing={2}>
            <Typography body1 long>
              Only one guardian will be able to access this minor's information.
              Guardianship cannot be changed once it is set.
            </Typography>
          </Grid>
        </Modal.Content>
        <Modal.Actions style={{ marginTop: 16 }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <Button secondary onClick={cancel}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={openDocument}>Acknowledge</Button>
            </Grid>
          </Grid>
        </Modal.Actions>
      </Modal>
      <Modal fullScreen={true} open={show}>
        <Modal.Content>
          <DocumentSigner
            params={params}
            documents={[template]}
            actor={actor}
            getDocumentImageUrls={({ documentId, page }) =>
              getDocumentPages({ documentId, page, uid: actor.uid })
            }
            actions={{
              submit: {
                text: 'Submit',
                action: docs => submit(docs[0]),
              },
              cancel: {
                text: 'Cancel',
                action: cancel,
              },
            }}
            throwFatalError={err => logger.error(err)}
          />
        </Modal.Content>
      </Modal>
    </>
  );
};

export default Affidavit;
